import { AfterViewInit, Component, Signal, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatOptionModule } from '@angular/material/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { FaIconLibrary, FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { SumBarChartComponent } from '../../sum-bar-chart/sum-bar-chart.component';
import { DateTime } from 'luxon';
import { AggregateResponse } from '@r1-compass-library/models/reporting/aggregate-response.model';
import { MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { faTimes } from '@fortawesome/pro-solid-svg-icons';
import { FocusMonitor } from '@angular/cdk/a11y';
import { CommonModule } from '@angular/common';
import { BehaviorSubject, switchMap } from 'rxjs';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { GroupedCountsService } from '../../grouped-counts.service';
import { filterNil } from 'ngxtension/filter-nil';
import { CountData, CountHeader, PlayResultDetailsActionType } from '@r1-compass-library/models';

export interface RecommendationsDashboardData {
    header: CountHeader;
    count: CountData;
    selectedDivisionIds: number[];
    actionType: PlayResultDetailsActionType;
}

@Component({
    selector: 'r1-compass-app-recommendations-dashboard',
    imports: [
        FontAwesomeModule,
        FormsModule,
        MatButtonModule,
        MatDialogModule,
        MatFormFieldModule,
        MatOptionModule,
        MatSelectModule,
        SumBarChartComponent,
        CommonModule,
    ],
    templateUrl: './recommendations-dashboard.component.html',
    styleUrl: './recommendations-dashboard.component.scss',
})
export class RecommendationsDashboardComponent implements AfterViewInit {
    public readonly data = inject<RecommendationsDashboardData>(MAT_DIALOG_DATA);
    readonly #_focusMonitor = inject(FocusMonitor);
    readonly #library = inject(FaIconLibrary);
    readonly #dashboardService = inject(GroupedCountsService);

    private dateTimeNow = DateTime.now();

    dateFilters: Map<string, DateTime> = new Map([
        ['Last 30 minutes', this.dateTimeNow.minus({ minutes: 30 })],
        ['Last 1 hour', this.dateTimeNow.minus({ hours: 1 })],
        ['Last 2 hours', this.dateTimeNow.minus({ hours: 2 })],
        ['Last 4 hours', this.dateTimeNow.minus({ hours: 4 })],
        ['Last 8 hours', this.dateTimeNow.minus({ hours: 8 })],
        ['Last 24 hours', this.dateTimeNow.minus({ days: 1 })],
        ['Last 48 hours', this.dateTimeNow.minus({ days: 2 })],
        ['Last 7 days', this.dateTimeNow.minus({ days: 7 })],
        ['Last 30 days', this.dateTimeNow.minus({ days: 30 })],
        ['Last 3 months', this.dateTimeNow.minus({ months: 3 })],
    ]);

    selectedFilter$ = new BehaviorSubject<string | null>(this.dateTimeNow.minus({ days: 1 }).toISO());
    groupedCounts: Signal<AggregateResponse[]> = toSignal(
        this.selectedFilter$.pipe(
            filterNil(),
            switchMap(filter =>
                this.#dashboardService.groupedCounts(
                    filter,
                    this.data.selectedDivisionIds,
                    'numRecordsSuccessful',
                    'numRecordsSuccessful',
                    this.data.actionType
                )
            )
        ),
        { initialValue: [] }
    );

    constructor() {
        const library = this.#library;

        library.addIcons(faTimes);
    }

    ngAfterViewInit() {
        const nodes: NodeListOf<HTMLElement> = document.querySelectorAll('.close-button') as NodeListOf<HTMLElement>;
        nodes.forEach(node => {
            this.#_focusMonitor.stopMonitoring(node);
        });
    }
}
